import { useState, useEffect } from 'react';
import { Package, AlertTriangle, Truck, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { PURCHASING_ENABLED } from '@/config/features';

interface InventoryStats {
  total_shipped: number;
  total_activated: number;
  available: number;
}

interface MedallionOrder {
  id: string;
  quantity: number;
  total_cents: number;
  status: 'pending' | 'approved' | 'shipped' | 'cancelled' | 'pending_payment' | 'accepted';
  tracking_number: string | null;
  created_at: string;
  shipped_at: string | null;
  payment_method: string | null;
}

const MIN_QUANTITY = 10;

const PortalInventory = () => {
  const { company, isOwner, hasPermission } = usePortalAuth();
  const unitPriceCents = company?.medallion_unit_price_cents ?? 2500;
  const { isReadOnly } = useReadOnly();
  const canOrderMedallions = isOwner || hasPermission('order_medallions');
  const { user, session } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inventory, setInventory] = useState<InventoryStats | null>(null);
  const [orders, setOrders] = useState<MedallionOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Order form state - simplified
  const [quantity, setQuantity] = useState(MIN_QUANTITY);
  const [instructions, setInstructions] = useState('');

  // Handle payment success/cancel from URL params
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const paymentCancelled = searchParams.get('payment_cancelled');
    const orderId = searchParams.get('order_id');

    if (paymentSuccess === 'true') {
      toast.success('Payment initiated successfully! Your order will be processed once the bank transfer completes.');
      // Clean up URL params
      searchParams.delete('payment_success');
      searchParams.delete('order_id');
      setSearchParams(searchParams, { replace: true });
    } else if (paymentCancelled === 'true') {
      toast.info('Payment was cancelled. You can try again when ready.');
      searchParams.delete('payment_cancelled');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchData = async () => {
      if (!company?.id) return;

      try {
        // Fetch inventory stats using RPC
        const { data: inventoryData, error: inventoryError } = await supabase
          .rpc('calculate_company_medallion_inventory', { _company_id: company.id });

        if (inventoryError) {
          console.error('Error fetching inventory:', inventoryError);
        } else if (inventoryData && inventoryData.length > 0) {
          setInventory(inventoryData[0]);
        } else {
          setInventory({ total_shipped: 0, total_activated: 0, available: 0 });
        }

        // Fetch order history
        const { data: ordersData, error: ordersError } = await supabase
          .from('medallion_orders')
          .select('id, quantity, total_cents, status, tracking_number, created_at, shipped_at, payment_method')
          .eq('monument_company_id', company.id)
          .order('created_at', { ascending: false });

        if (ordersError) {
          console.error('Error fetching orders:', ordersError);
        } else {
          setOrders(ordersData || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (company?.id) {
      fetchData();
    }
  }, [company?.id]);

  const handlePayWithACH = async () => {
    if (!company?.id || !user?.id || !session?.access_token) return;

    if (quantity < MIN_QUANTITY) {
      toast.error(`Minimum order quantity is ${MIN_QUANTITY} medallions`);
      return;
    }

    if (quantity > 1000) {
      toast.error('Maximum order quantity is 1000 medallions');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await supabase.functions.invoke('create-medallion-checkout', {
        body: {
          monumentCompanyId: company.id,
          quantity,
          specialInstructions: instructions.trim() || null,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create checkout session');
      }

      const { checkoutUrl } = response.data;

      if (!checkoutUrl) {
        throw new Error('No checkout URL received');
      }

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (error: unknown) {
      console.error('Error creating checkout:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout process');
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending Approval</Badge>;
      case 'pending_payment':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Payment Processing</Badge>;
      case 'accepted':
        return <Badge className="bg-blue-500 hover:bg-blue-600">Payment Received</Badge>;
      case 'approved':
        return <Badge className="bg-blue-500 hover:bg-blue-600">Approved</Badge>;
      case 'shipped':
        return <Badge className="bg-green-600 hover:bg-green-700">Shipped</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const lowStock = (inventory?.available || 0) < 10;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Medallion Inventory</h2>
        <p className="text-muted-foreground">
          Track your medallion stock and place orders for more.
        </p>
      </div>

      {/* Low Stock Warning */}
      {lowStock && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Low stock! You have {inventory?.available || 0} medallions available. Consider placing an order.
          </AlertDescription>
        </Alert>
      )}

      {/* Inventory Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${lowStock ? 'text-destructive' : ''}`}>
              {inventory?.available || 0}
            </div>
            <p className="text-xs text-muted-foreground">Ready to assign</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Shipped</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inventory?.total_shipped || 0}</div>
            <p className="text-xs text-muted-foreground">Medallions received</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Activated</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inventory?.total_activated || 0}</div>
            <p className="text-xs text-muted-foreground">Assigned to memorials</p>
          </CardContent>
        </Card>
      </div>

      {/* Order Form - Owner or staff with order_medallions permission (hidden
          when deactivated, or when purchasing is switched off entirely) */}
      {PURCHASING_ENABLED && canOrderMedallions && !isReadOnly && (
        <Card>
          <CardHeader>
            <CardTitle>Order Medallions</CardTitle>
            <CardDescription>
              Order additional medallions via secure bank transfer (ACH). Minimum order: {MIN_QUANTITY} medallions at ${(unitPriceCents / 100).toFixed(2)} each.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity (minimum {MIN_QUANTITY})</Label>
                <Input
                  id="quantity"
                  type="number"
                  min={1}
                  max={1000}
                  value={quantity || ''}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                  className={quantity > 0 && quantity < MIN_QUANTITY ? "border-destructive focus-visible:ring-destructive" : ""}
                />
                {quantity > 0 && quantity < MIN_QUANTITY && (
                  <p className="text-sm text-destructive">MOQ is {MIN_QUANTITY}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <div className="text-2xl font-bold">
                  ${((quantity * unitPriceCents) / 100).toFixed(2)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions">Special Instructions (Optional)</Label>
              <Textarea
                id="instructions"
                placeholder="Any special instructions for your order"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={2}
              />
            </div>

            <Alert>
              <CreditCard className="h-4 w-4" />
              <AlertDescription>
                <strong>Bank Transfer (ACH)</strong> - You'll enter your shipping address on the next page. 
                ACH payments typically take 3-5 business days to process.
              </AlertDescription>
            </Alert>

            <Button
              onClick={handlePayWithACH}
              disabled={isSubmitting || quantity < MIN_QUANTITY}
              className="w-full md:w-auto"
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Preparing Checkout...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay via Bank Transfer
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Order History - Only visible to those with order_medallions permission */}
      {canOrderMedallions && (
        <Card>
          <CardHeader>
            <CardTitle>Order History</CardTitle>
            <CardDescription>
              Your previous medallion orders
            </CardDescription>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No orders yet.
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden sm:table-cell">Tracking</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="whitespace-nowrap">
                          {new Date(order.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{order.quantity}</TableCell>
                        <TableCell>${(order.total_cents / 100).toFixed(2)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {getStatusBadge(order.status)}
                            {/* Show tracking on mobile below status */}
                            {order.tracking_number && (
                              <span className="text-xs text-muted-foreground sm:hidden">
                                📦 {order.tracking_number}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {order.tracking_number ? (
                            <span className="text-sm">{order.tracking_number}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortalInventory;
